import type {
  VenueClosureApplyScope,
  VenueClosureCreateInput,
  VenueClosureItemView,
  VenueClosureType,
} from './closureTypes'
import { EMPTY_MARK } from './format'

/**
 * 휴무·차단·레슨 등록 폼 상태 ↔ `VenueClosureCreateInput` 매핑 + 사전 검증 + 목록 필터링(설계 §2).
 *
 * 🚨 `if (type === ...)` 를 컴포넌트에 흩지 않는다 — `courtViewModel.ts`/`operatingHourViewModel.ts`
 * 가 세운 규칙 승계.
 *
 * 🚨 유형별 필수조합의 **최종 판정처는 서버 도메인**(`VenueClosure.init`)이다. 여기 검증은 왕복을
 * 줄이기 위한 클라이언트 사전 검증이며, 서버 계약(#604 설계 §2 매트릭스)과 **동일한 규칙**을 미러링한다.
 */

/** 요일 토글 칩 라벨. 🚨 등록 폼은 아직 서버 응답이 없어(빈 폼) 라벨을 받을 곳이 없다 — 이 화면 UI 전용
 *  예외로 하드코딩한다(제출 페이로드에는 `dayOfWeek` 코드만 실린다, 라벨은 담지 않는다). */
export const CLOSURE_WEEKDAY_CHIPS: { value: string; label: string }[] = [
  { value: 'MONDAY', label: '월' },
  { value: 'TUESDAY', label: '화' },
  { value: 'WEDNESDAY', label: '수' },
  { value: 'THURSDAY', label: '목' },
  { value: 'FRIDAY', label: '금' },
  { value: 'SATURDAY', label: '토' },
  { value: 'SUNDAY', label: '일' },
]

/** 숫자 입력과 마찬가지로 날짜·시간도 빈 문자열이 유효한 중간 상태라 텍스트로 다룬다. */
export interface ClosureFormState {
  type: VenueClosureType
  /** HOLIDAY·BLOCK 의 시작일. BLOCK 은 제출 시 종료일과 같은 값으로 매핑된다(설계 §2 자율 결정 A3). */
  date: string
  /** HOLIDAY 전용 종료일. 비어 있으면 `date` 와 같은 값으로 취급한다. */
  endDate: string
  /** RECURRING_BLOCK·LESSON 전용. */
  recurringDayOfWeeks: string[]
  /** BLOCK·RECURRING_BLOCK·LESSON 전용. "HH:mm". */
  startTime: string
  endTime: string
  applyScope: VenueClosureApplyScope
  courtIds: number[]
  /** LESSON 전용. */
  title: string
  /** BLOCK·RECURRING_BLOCK 은 필수, HOLIDAY·LESSON 은 선택. */
  reason: string
}

const DEFAULT_START_TIME = '09:00'
const DEFAULT_END_TIME = '10:00'

export function emptyClosureFormState(type: VenueClosureType = 'HOLIDAY'): ClosureFormState {
  return {
    type,
    date: '',
    endDate: '',
    recurringDayOfWeeks: [],
    startTime: DEFAULT_START_TIME,
    endTime: DEFAULT_END_TIME,
    applyScope: 'ALL_COURTS',
    courtIds: [],
    title: '',
    reason: '',
  }
}

/** 유형 탭 전환. 값 자체는 보존한다 — 서버 제출 매핑([toClosureCreateInput])이 유형에 맞는 필드만 골라 쓴다. */
export function switchClosureFormType(form: ClosureFormState, type: VenueClosureType): ClosureFormState {
  return { ...form, type }
}

export function toggleClosureWeekday(form: ClosureFormState, dayOfWeek: string): ClosureFormState {
  const has = form.recurringDayOfWeeks.includes(dayOfWeek)
  return {
    ...form,
    recurringDayOfWeeks: has
      ? form.recurringDayOfWeeks.filter((day) => day !== dayOfWeek)
      : [...form.recurringDayOfWeeks, dayOfWeek],
  }
}

export function toggleClosureCourt(form: ClosureFormState, courtId: number): ClosureFormState {
  const has = form.courtIds.includes(courtId)
  return {
    ...form,
    courtIds: has ? form.courtIds.filter((id) => id !== courtId) : [...form.courtIds, courtId],
  }
}

/** 폼 → `POST` 요청 바디(`force` 제외 — 확인 모달의 재시도 흐름이 그 값을 따로 붙인다). */
export function toClosureCreateInput(form: ClosureFormState): Omit<VenueClosureCreateInput, 'force'> {
  const trimmedReason = form.reason.trim()
  const courtIds = form.applyScope === 'ALL_COURTS' ? [] : form.courtIds

  switch (form.type) {
    case 'HOLIDAY':
      return {
        type: 'HOLIDAY',
        startDate: form.date,
        endDate: form.endDate.trim().length > 0 ? form.endDate : form.date,
        recurringDayOfWeeks: [],
        startTime: null,
        endTime: null,
        applyScope: form.applyScope,
        courtIds,
        title: null,
        reason: trimmedReason.length > 0 ? trimmedReason : null,
      }
    case 'BLOCK':
      return {
        type: 'BLOCK',
        startDate: form.date,
        // 🚨 등록 폼은 단일 날짜만 받는다 — 시작일=종료일로 매핑한다(설계 §2 자율 결정 A3).
        endDate: form.date,
        recurringDayOfWeeks: [],
        startTime: form.startTime,
        endTime: form.endTime,
        applyScope: form.applyScope,
        courtIds,
        title: null,
        reason: trimmedReason,
      }
    case 'RECURRING_BLOCK':
      return {
        type: 'RECURRING_BLOCK',
        startDate: null,
        endDate: null,
        recurringDayOfWeeks: form.recurringDayOfWeeks,
        startTime: form.startTime,
        endTime: form.endTime,
        applyScope: form.applyScope,
        courtIds,
        title: null,
        reason: trimmedReason,
      }
    case 'LESSON':
      return {
        type: 'LESSON',
        startDate: null,
        endDate: null,
        recurringDayOfWeeks: form.recurringDayOfWeeks,
        startTime: form.startTime,
        endTime: form.endTime,
        applyScope: form.applyScope,
        courtIds,
        title: form.title.trim(),
        reason: trimmedReason.length > 0 ? trimmedReason : null,
      }
  }
}

export interface ClosureFormErrors {
  date?: string
  endDate?: string
  recurringDayOfWeeks?: string
  time?: string
  courts?: string
  title?: string
  reason?: string
}

export function hasClosureFormErrors(errors: ClosureFormErrors): boolean {
  return Object.keys(errors).length > 0
}

/** "HH:mm" → 자정으로부터의 분. "24:00" 은 1440(`operatingHourViewModel.ts` 규칙 승계). */
function toMinutes(time: string): number {
  const [hoursText, minutesText] = time.split(':')
  return Number(hoursText ?? 0) * 60 + Number(minutesText ?? 0)
}

function validateApplyScope(form: ClosureFormState, errors: ClosureFormErrors): void {
  if (form.applyScope === 'SELECTED_COURTS' && form.courtIds.length === 0) {
    errors.courts = '적용 코트를 1개 이상 선택해 주세요.'
  }
}

function validateTimeRange(form: ClosureFormState, errors: ClosureFormErrors): void {
  if (form.startTime.length === 0 || form.endTime.length === 0) {
    errors.time = '시작·종료 시각을 입력해 주세요.'
    return
  }
  if (toMinutes(form.endTime) <= toMinutes(form.startTime)) {
    errors.time = '종료 시각은 시작 시각보다 뒤여야 합니다.'
  }
}

/** 클라이언트 사전 검증(#604 설계 §2 유형별 필수조합 매트릭스 미러링). 서버가 최종 판정자다. */
export function validateClosureForm(form: ClosureFormState): ClosureFormErrors {
  const errors: ClosureFormErrors = {}

  switch (form.type) {
    case 'HOLIDAY': {
      if (form.date.length === 0) errors.date = '시작일을 선택해 주세요.'
      if (
        form.date.length > 0 &&
        form.endDate.length > 0 &&
        form.endDate < form.date
      ) {
        errors.endDate = '종료일은 시작일과 같거나 이후여야 합니다.'
      }
      validateApplyScope(form, errors)
      return errors
    }
    case 'BLOCK': {
      if (form.date.length === 0) errors.date = '날짜를 선택해 주세요.'
      validateTimeRange(form, errors)
      if (form.reason.trim().length === 0) errors.reason = '차단 사유를 입력해 주세요.'
      validateApplyScope(form, errors)
      return errors
    }
    case 'RECURRING_BLOCK': {
      if (form.recurringDayOfWeeks.length === 0) {
        errors.recurringDayOfWeeks = '반복 요일을 1개 이상 선택해 주세요.'
      }
      validateTimeRange(form, errors)
      if (form.reason.trim().length === 0) errors.reason = '차단 사유를 입력해 주세요.'
      validateApplyScope(form, errors)
      return errors
    }
    case 'LESSON': {
      if (form.recurringDayOfWeeks.length === 0) {
        errors.recurringDayOfWeeks = '반복 요일을 1개 이상 선택해 주세요.'
      }
      validateTimeRange(form, errors)
      if (form.title.trim().length === 0) errors.title = '레슨 이름을 입력해 주세요.'
      validateApplyScope(form, errors)
      return errors
    }
  }
}

/** 등록 확인 모달 문구 그룹 — "휴무형" 은 HOLIDAY, "차단형" 은 나머지 3유형(설계 §2 자율 결정 A2). */
export function closureConfirmMessageGroup(type: VenueClosureType): 'HOLIDAY' | 'BLOCK_LIKE' {
  return type === 'HOLIDAY' ? 'HOLIDAY' : 'BLOCK_LIKE'
}

export interface ClosureFilter {
  type: VenueClosureType | 'ALL'
  courtId: number | 'ALL'
}

export const EMPTY_CLOSURE_FILTER: ClosureFilter = { type: 'ALL', courtId: 'ALL' }

/**
 * 목록 클라이언트 필터링(설계 §2 자율 결정 A1). 서버 조회는 무필터로 받고 이 함수가 화면 필터를 적용한다.
 * 🚨 코트 필터는 `ALL_COURTS` 항목도 포함한다 — 판정은 "그 코트에 적용되는가" 다(서버 `appliesTo` 규칙과
 * 동일하게 재현).
 */
export function filterClosureItems(
  items: VenueClosureItemView[],
  filter: ClosureFilter
): VenueClosureItemView[] {
  return items.filter((item) => {
    if (filter.type !== 'ALL' && item.type !== filter.type) return false
    if (filter.courtId === 'ALL') return true
    return item.applyScope === 'ALL_COURTS' || item.courts.some((court) => court.courtId === filter.courtId)
  })
}

/** 표의 "대상 코트" 칸. */
export function closureCourtsText(item: VenueClosureItemView): string {
  if (item.applyScope === 'ALL_COURTS') return '전체 코트'
  if (item.courts.length === 0) return EMPTY_MARK
  return item.courts.map((court) => court.courtName).join(', ')
}

/** 표의 "사유/레슨명" 칸 — 레슨은 `reason` 이 없고 `title` 만 있다. */
export function closureReasonOrTitleText(item: VenueClosureItemView): string {
  return item.reason ?? item.title ?? EMPTY_MARK
}
